import React, { useState, useEffect, useRef } from "react";
import SequenceableControls from "./SequenceableControls";
import DrEditor from "./Editor";
import Spinner from "../../Spinner";
import SectionWithAdd from "./SectionWithAdd";
import StatefulRichTextList from "./StatefulRichTextList";

/**
 * <RichTextList syllabusId={} api={} type={} plType={} lookup_type_id={optional} editMode={} reqOptions={} focus={} focusKey={} lastUpdateType={} defaultText={} containerId={} canViewProgress={} canEditProgress={} progressA={} progAIndex={} toggleProgressA={} addItem={} />
 */
const StatefulSimpleSection = (props) => {
  try {
    const _isMounted = useRef(false);
    const {
      childSectionName,
      syllabusId,
      progAIndex,
      api,
      editMode,
      type,
      plType,
      childType,
      plChildType,
      reqOptions,
      lastUpdateType,
      updateTime,
      typeLookupId,
      sectionName,
    } = props;
    const url = `${api}${plType}`;
    const [items, setItems] = useState(props.items || []);
    const [maxSequence, setMaxSequence] = useState(items.length);
    const [dataReady, setDataReady] = useState(!!props.items);
    const [reload, setReload] = useState(!props.items);

    useEffect(function updateMounted() {
      _isMounted.current = true;
      return function cleanupMounted() {
        _isMounted.current = false;
      };
    }, []);

    useEffect(
      function itemPropUpdate() {
        setItems(props.items);
        setMaxSequence(props.items.length);
      },
      [props.items]
    );

    useEffect(
      function fetchItems() {
        if (reload) {
          apiCall(
            `${url}?${type}[syllabus_id]=${syllabusId}&${type}[type_lookup_id]=${typeLookupId}`,
            reqOptions("GET"),
            (res) => {
              res.json().then((data) => {
                if (_isMounted.current) {
                  setItems(data);
                  setMaxSequence(data.length);
                  setDataReady(true);
                  setReload(false);
                }
              });
            }
          );
        }
      },
      [reload]
    );

    useEffect(
      function reloadEffect() {
        if (
          (lastUpdateType === type ||
            lastUpdateType === `${type}-${typeLookupId}`) &&
          _isMounted
        ) {
          setReload(true);
        }
      },
      [updateTime]
    );

    const addItem = (opts, e) => {
      console.log("HERE IS WHERE WE ADD A TOP LEVEL ITEM");
      const body = {};
      body[type] = {
        syllabus_id: syllabusId,
        sequence: opts["sequence"],
        type_lookup_id: typeLookupId,
      };
      apiCall(url, reqOptions("POST", body), (res) => setReload(true));
    };

    const removeItem = (opts, e) => {
      if (confirm("Are you sure you want to remove this item?")) {
        const { id } = opts;
        const body = {};
        body[type] = { syllabus_id: syllabusId, active: false };
        apiCall(`${url}/${id}`, reqOptions("PATCH", body), (res) =>
          setReload(true)
        );
      }
    };

    const updateItemSequence = (opts, e) => {
      const { id, index, value } = opts;
      const body = {};
      body[type] = { syllabus_id: syllabusId, sequence: value };
      apiCall(`${url}/${id}`, reqOptions("PATCH", body), (res) =>
        setReload(true)
      );
    };

    const display =
      items.length > 0 || editMode ? (
        <SectionWithAdd
          {...props}
          id={`${type}-section${typeLookupId}`}
          type={type}
          plType={plType}
          showProgress={props.canViewProgress}
          progIndex={progAIndex}
          addItem={addItem}
          sectionName={sectionName}
        >
          <ul className="list-group top-buffer sm-font">
            {items.map((i, ix) => (
              <StatefulSimpleText
                {...props}
                key={`${type}-${i.id}`}
                parentKey={`${type}-${i.id}`}
                item={i}
                description={i.description || ""}
                ix={ix}
                maxSequence={maxSequence}
                handleChange={updateItemSequence}
                removeItem={removeItem}
                dataReady={dataReady}
              />
            ))}
          </ul>
        </SectionWithAdd>
      ) : (
        ""
      );

    return dataReady ? display : <Spinner />;
  } catch (e) {
    console.error(e);
  }
};

/**
 * <StatefulSimpleText {...props} item={} maxSequence={} ix={} />
 */
const StatefulSimpleText = (props) => {
  try {
    const {
      api,
      childSectionName,
      editMode,
      item,
      syllabusId,
      type,
      plType,
      childType,
      plChildType,
      reqOptions,
      maxSequence,
      ix,
      defaultText,
      focusKey,
      dataReady,
      parentKey,
      containerId,
      updateTime,
    } = props;
    const url = `${api}${plType}/${item.id}`;
    const [description, setDescription] = useState(props.description || "");
    const [timer, setTimer] = useState(null);
    const _isMounted = useRef(false);
    const _localUpdate = useRef(false);
    const inFocus = focusKey === parentKey;

    const updateDescription = (e) => {
      if (description != e.target.value) {
        _localUpdate.current = true;
        setDescription(e.target.value);
      }
    };

    const debounce = (fn) => {
      if (timer) clearTimeout(timer);
      if (_isMounted.current) {
        setTimer(
          setTimeout(() => {
            fn();
          }, 300)
        );
      }
    };

    useEffect(() => {
      _isMounted.current = true;
      return () => {
        console.log("cleaup");
        _isMounted.current = false;
      };
    }, []);

    useEffect(() => {
      if (_localUpdate.current) {
        const body = { container_id: containerId };
        body[type] = { syllabus_id: syllabusId, description: description };
        debounce(() => {
          apiCall(url, reqOptions("PATCH", body));
        });
      }
    }, [description, props.description]);

    useEffect(() => {
      _localUpdate.current = false;
      setDescription(props.description);
    }, [props.description]);

    const childList =
      childType && editMode ? (
        <div className="ml-4">
          <StatefulRichTextList
            {...props}
            key={`${type}-${item.id}-${childType}`}
            items={item[plChildType]}
            listTitle={childSectionName}
            defaultText={`${childSectionName}...`}
            parentId={item.id}
            parentType={type}
            type={childType}
            plType={plChildType}
          />
        </div>
      ) : (
        ""
      );

    return (
      <li id={parentKey} className={`test-${type} list-group-item`}>
        <div className="d-flex justify-content-between align-items-top">
          <div
            className="d-flex justify-content-left w-75 align-items-top"
            onClick={(e) => props.focus(parentKey, e)}
          >
            <span className="xs-font pr-0 pt-1">{item.sequence}.</span>
            {inFocus ? (
              <textarea
                id={parentKey + "nameinput"}
                className="col-11"
                placeholder={`${props.sectionName} description`}
                onChange={(e) => updateDescription(e)}
                value={description}
              />
            ) : (
              <div className="col-11">{description}</div>
            )}
          </div>
          <SequenceableControls
            {...props}
            itemIndex={ix}
            parentKey={parentKey}
            maxSequence={maxSequence}
          />
        </div>
        {childList}
      </li>
    );
  } catch (e) {
    console.error(e);
  }
};

const apiCall = (url, reqOptions, next) => {
  try {
    fetch(url, reqOptions).then((res) => {
      if (res.status === 401) {
        // alert('Unauthorized. Either your session has expired or you are not authorized to perform this action. Please try logging out and logging in again.')
        // if (confirm('Unauthorized. Either your session has expired or you are not authorized to perform this action. Redirect to login page? \n(Please note: if your session has expired, none of the changes you make to this syllabus will be saved until you log in again.)')) {
        //   sylContainer.props.history.push('/log_in');
        // }
        alert(
          "Not authorized to update the syllabus. Please reload the page and try again."
        );
      } else if (res.status >= 400) {
        alert(
          "There was an issue updating the syllabus. Please reload the page and try again."
        );
      } else if (next) {
        next(res);
      }
    });
  } catch (err) {
    //To Do: handle error
  }
};

export default StatefulSimpleSection;
